import { useRef, useState, useEffect } from 'react'
import SignatureCanvas from 'react-signature-canvas'

interface SignaturePadProps {
  width?: number
  height?: number
  penColor?: string
  backgroundColor?: string
  onSignatureChange?: (signature: string | null) => void
  initialSignature?: string
  disabled?: boolean
  className?: string
  showError?: boolean
}

export default function SignaturePad({
  width = 400,
  height = 200,
  penColor = '#000000',
  backgroundColor = '#ffffff',
  onSignatureChange,
  initialSignature,
  disabled = false,
  className = '',
  showError = false
}: SignaturePadProps) {
  const signatureRef = useRef<SignatureCanvas>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isEmpty, setIsEmpty] = useState(true)
  const [isDragOver, setIsDragOver] = useState(false)
  const [signatureMode, setSignatureMode] = useState<'draw' | 'upload'>('draw')

  useEffect(() => {
    if (initialSignature && signatureRef.current) {
      try {
        signatureRef.current.fromDataURL(initialSignature)
        setIsEmpty(false)
      } catch (error) {
        console.warn('Failed to load initial signature:', error)
      }
    }
  }, [initialSignature])

  const processImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file (PNG, JPG, GIF, etc.)')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string
      if (dataUrl && signatureRef.current) {
        const img = new Image()
        img.onload = () => {
          const canvas = signatureRef.current?.getCanvas()
          if (canvas) {
            const ctx = canvas.getContext('2d')
            if (ctx) {
              ctx.clearRect(0, 0, canvas.width, canvas.height)
              
              // Calculate dimensions to fit image within canvas while maintaining aspect ratio
              const canvasRatio = canvas.width / canvas.height
              const imageRatio = img.width / img.height
              
              let drawWidth = canvas.width
              let drawHeight = canvas.height
              let offsetX = 0
              let offsetY = 0
              
              if (imageRatio > canvasRatio) {
                // Image is wider, fit by width
                drawHeight = canvas.width / imageRatio
                offsetY = (canvas.height - drawHeight) / 2
              } else {
                // Image is taller, fit by height
                drawWidth = canvas.height * imageRatio
                offsetX = (canvas.width - drawWidth) / 2
              }
              
              ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight)
              setIsEmpty(false)
              onSignatureChange?.(dataUrl)
            }
          }
        }
        img.src = dataUrl
      }
    }
    reader.readAsDataURL(file)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      processImageFile(file)
    }
    // Reset the input value so the same file can be selected again
    e.target.value = ''
  }

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Only set to false if we're leaving the actual drop zone
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      processImageFile(files[0])
    }
  }

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear()
      setIsEmpty(true)
      onSignatureChange?.(null)
    }
  }

  const handleUndo = () => {
    // Note: react-signature-canvas doesn't have built-in undo
    // We implement a simple clear for now
    handleClear()
  }

  const handleEnd = () => {
    if (signatureRef.current) {
      const currentIsEmpty = signatureRef.current.isEmpty()
      setIsEmpty(currentIsEmpty)
      
      if (!currentIsEmpty) {
        // Get the canvas and create a new one with transparent background
        const originalCanvas = signatureRef.current.getCanvas()
        const tempCanvas = document.createElement('canvas')
        const tempCtx = tempCanvas.getContext('2d')
        
        tempCanvas.width = originalCanvas.width
        tempCanvas.height = originalCanvas.height
        
        // Don't fill background - leave it transparent
        // Copy only the signature data
        tempCtx.drawImage(originalCanvas, 0, 0)
        
        // Export with transparent background
        const dataURL = tempCanvas.toDataURL('image/png')
        onSignatureChange?.(dataURL)
      } else {
        onSignatureChange?.(null)
      }
    }
  }

  const handleSave = () => {
    if (signatureRef.current && !isEmpty) {
      // Get the canvas and create a new one with transparent background
      const originalCanvas = signatureRef.current.getCanvas()
      const tempCanvas = document.createElement('canvas')
      const tempCtx = tempCanvas.getContext('2d')
      
      tempCanvas.width = originalCanvas.width
      tempCanvas.height = originalCanvas.height
      
      // Don't fill background - leave it transparent
      // Copy only the signature data
      tempCtx.drawImage(originalCanvas, 0, 0)
      
      // Export with transparent background
      const dataURL = tempCanvas.toDataURL('image/png')
      onSignatureChange?.(dataURL)
      return dataURL
    }
    return null
  }

  const canvasProps = {
    penColor,
    backgroundColor: backgroundColor === 'transparent' ? 'rgba(0,0,0,0)' : backgroundColor,
    canvasProps: {
      width,
      height,
      className: 'signature-canvas'
    },
    onEnd: handleEnd
  }

  return (
    <div className={`signature-pad ${className}`}>
      {!disabled && (
        <div style={{ 
          marginBottom: '16px', 
          display: 'flex', 
          gap: '8px',
          justifyContent: 'center'
        }}>
          <button
            type="button"
            className={`button ${signatureMode === 'draw' ? '' : 'secondary'}`}
            onClick={() => setSignatureMode('draw')}
            style={{
              padding: '8px 16px',
              fontSize: '14px'
            }}
          >
            ✏️ Draw Signature
          </button>
          
          <button
            type="button"
            className={`button ${signatureMode === 'upload' ? '' : 'secondary'}`}
            onClick={() => setSignatureMode('upload')}
            style={{
              padding: '8px 16px',
              fontSize: '14px'
            }}
          >
            📤 Upload Image
          </button>
        </div>
      )}

      <div
        style={{
          border: `2px ${isDragOver ? 'dashed #007bff' : (showError && isEmpty ? 'solid #dc3545' : 'solid #ddd')}`,
          borderRadius: '8px',
          position: 'relative',
          display: 'inline-block',
          backgroundColor: isDragOver ? '#f0f8ff' : (backgroundColor === 'transparent' ? 'transparent' : backgroundColor),
          transition: 'all 0.2s ease'
        }}
        onDragEnter={!disabled && signatureMode === 'upload' ? handleDragEnter : undefined}
        onDragLeave={!disabled && signatureMode === 'upload' ? handleDragLeave : undefined}
        onDragOver={!disabled && signatureMode === 'upload' ? handleDragOver : undefined}
        onDrop={!disabled && signatureMode === 'upload' ? handleDrop : undefined}
      >
        <SignatureCanvas
          ref={signatureRef}
          {...canvasProps}
          {...(disabled && { 
            canvasProps: { 
              ...canvasProps.canvasProps,
              style: { pointerEvents: 'none', opacity: 0.6 }
            }
          })}
          {...(signatureMode === 'upload' && !disabled && { 
            canvasProps: { 
              ...canvasProps.canvasProps,
              style: { pointerEvents: 'none' }
            }
          })}
        />
        
        {isEmpty && !disabled && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#999',
              fontSize: '16px',
              pointerEvents: 'none',
              textAlign: 'center'
            }}
          >
            {signatureMode === 'draw' ? 'Sign here' : (
              <div>
                {isDragOver ? (
                  <div>
                    📤 Drop image here
                  </div>
                ) : (
                  <div>
                    Drag & drop signature image<br />
                    or click "Browse" below
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {isDragOver && !disabled && signatureMode === 'upload' && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 123, 255, 0.1)',
              border: '2px dashed #007bff',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              color: '#007bff',
              fontWeight: 'bold',
              pointerEvents: 'none'
            }}
          >
            📤 Drop image here
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />
      
      {!disabled && (
        <div style={{ 
          marginTop: '12px', 
          display: 'flex', 
          gap: '8px',
          justifyContent: 'center'
        }}>
          {signatureMode === 'draw' ? (
            <>
              <button
                type="button"
                className="button secondary"
                onClick={handleClear}
                style={{
                  padding: '6px 12px',
                  fontSize: '14px',
                  minWidth: '80px'
                }}
              >
                Clear
              </button>
              
              <button
                type="button"
                className="button secondary"
                onClick={handleUndo}
                style={{
                  padding: '6px 12px',
                  fontSize: '14px',
                  minWidth: '80px'
                }}
              >
                Undo
              </button>
              
              {onSignatureChange && (
                <button
                  type="button"
                  className="button"
                  onClick={handleSave}
                  disabled={isEmpty}
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    minWidth: '80px',
                    opacity: isEmpty ? 0.5 : 1
                  }}
                >
                  Save
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className="button secondary"
                onClick={handleClear}
                style={{
                  padding: '6px 12px',
                  fontSize: '14px',
                  minWidth: '80px'
                }}
              >
                Clear
              </button>
              
              <button
                type="button"
                className="button"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: '6px 12px',
                  fontSize: '14px',
                  minWidth: '80px'
                }}
              >
                📁 Browse
              </button>
            </>
          )}
        </div>
      )}
      
      <style>{`
        .signature-canvas {
          border-radius: 6px;
        }
        
        .signature-pad .button {
          background-color: #007bff;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          padding: 6px 12px;
          transition: background-color 0.2s;
        }
        
        .signature-pad .button:hover:not(:disabled) {
          background-color: #0056b3;
        }
        
        .signature-pad .button:disabled {
          cursor: not-allowed;
        }
        
        .signature-pad .button.secondary {
          background-color: #6c757d;
        }
        
        .signature-pad .button.secondary:hover {
          background-color: #545b62;
        }
      `}</style>
    </div>
  )
}